import WhatsAppButton from './elements/WhatsAppButton';
import { ApiRequest } from './GlobalFunctions';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { ErrorBoundary } from 'elements/ErrorBoundary';

import Home from './components/Home/Home';
import Login from './components/Auth/Login';
import AccountNurses from 'components/Account/AccountNurses';
import AccountUsers from 'components/Account/AccountUsers';
import AccountEditor from 'components/Account/AccountEditor';
import NursesList from 'components/Nurses/NursesList';
import NursesDetail from 'components/Nurses/NursesDetail';
import Terms from 'Pages/Terms';
import PaymentResult from 'components/Payment/PaymentResult';
import MessagesList from './components/Messages/MessagesList';
import BannersList from 'components/Admin/Banners/BannersList';

import GlobalBlog from 'components/Blog/GlobalBlog';

import Users from 'components/Admin/Users/UsersList';
import BlogList from 'components/Admin/Blog/BlogList';
import AdminMessages from 'components/Admin/Messages/MessagesList';
import AboutUs from 'Pages/AboutUs';
import BlogDetails from 'components/Blog/BlogDetails';
import Settings from 'components/Admin/Settings/Settings'; 
import BadgesList from 'components/Admin/Badges/BadgesList';
import CompaniesList from 'components/Admin/Companies/CompaniesList';
import CompanyDetails from 'components/Companies/CompanyDetails';
import AdminRatingsList from 'components/Admin/Ratings/RatingsList';
import NursesReport from 'components/Admin/Nurses/NursesReport';
import RedirectsList from 'components/Admin/Redirects/RedirectsList';
import WhatsappLogsList from 'components/Admin/WhatsappLogs/WhatsappLogsList';
import FAQ from 'components/FAQ/FAQ';
import NurseQuoteView from 'components/Quotes/NurseQuoteView';
import PatientQuotesView from 'components/Quotes/PatientQuotesView';
import AdminQuoteView from 'components/Admin/Quotes/AdminQuoteView';
import QuoteRequestsList from 'components/Admin/Quotes/QuoteRequestsList';
import QuoteLanding from 'components/Quotes/QuoteLanding';
import TestFlujoSolicitud from 'Pages/TestFlujoSolicitud';

interface ModuleResponse {
  module: string;
  id?: string | number;
}

const componentMap: Record<string, any> = {
  'blog': BlogDetails,
  'nurses': NursesDetail
  // Puedes agregar más módulos aquí
};

/**
 * AppRoutes — definición de rutas de la aplicación.
 *
 * Usa `useAuth()` para determinar el rol del usuario en lugar de leer
 * localStorage directamente, garantizando reactividad al login/logout.
 *
 * Bug corregido: la dependencia del useEffect era `window.location.pathname`
 * (no reactivo); ahora usa `location.pathname` via `useLocation()`.
 */
export default function AppRoutes(props: any) {
  const { role_id } = useAuth();
  const { setLoading } = props;
  const location = useLocation();

  const [moduleData, setModuleData] = useState<ModuleResponse | null>(null);
  // checkedPath: última URL cuya resolución de alias terminó.
  // Si location.pathname !== checkedPath el resolver está en vuelo → mostrar null, nunca <Navigate to="/" />.
  const [checkedPath, setCheckedPath] = useState<string | null>(null);

  useEffect(() => {
    const currentPath = location.pathname;
    if (!isKnownRoute(currentPath)) {
      setModuleData(null);   // limpiar datos del alias anterior
      getAliasURl(currentPath);
    } else {
      // Rutas conocidas no necesitan resolver alias; marcar como resuelta.
      setCheckedPath(currentPath);
      setModuleData(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isKnownRoute = (path: string): boolean => {
    const knownRoutes = [
      '/',
      '/login',
      '/cuenta',
      '/cuenta/editar',
      '/enfermeras',
      '/terminos',
      '/quienes-somos',
      '/resultado-pago',
      '/mensajes',
      '/blog',
      '/banners',
      '/usuarios',
      '/admin-blog',
      '/admin-mensajes',
      '/configuracion',
      '/distintivos',
      '/empresas',
      '/empresas/:company_id',
      '/calificaciones',
      '/reporte-enfermeras',
      '/contactos-whatsapp',
      '/preguntas-frecuentes',
      '/test-flujo-solicitud',
      '/reporte-cotizaciones',
      '/cotiza-tu-enfermera'
    ];

    // Rutas de cotización (con token dinámico)
    if (path.startsWith('/ver-cotizacion/') || path.startsWith('/mis-cotizaciones/') || path.startsWith('/admin/cotizaciones/')) return true;

    // Rutas exactas
    if (knownRoutes.includes(path)) {
      return true;
    }

    // Rutas con parámetros válidos
    const validParameterRoutes = [
      { base: '/enfermeras/', paramRegex: /^\/enfermeras\/\d+$/ },
      { base: '/mensajes/', paramRegex: /^\/mensajes\/\d+$/ },
      { base: '/blog/', paramRegex: /^\/blog\/[a-zA-Z0-9-]+$/ },
      { base: '/empresas/', paramRegex: /^\/empresas\/\d+$/ }
    ];

    return validParameterRoutes.some(route => 
      path.startsWith(route.base) && route.paramRegex.test(path)
    );
  }

  const getAliasURl = (currentPath: string) => {
    ApiRequest({
      url: "/alias/getURL",
      method: "GET",
      query: { alias: currentPath },
      setResponse: (response: any) => {
        if (response.status === "success") {
          setModuleData({ module: response.module, id: response.id });
        }
        // Marcar esta URL como resuelta. Si status !== 'success', moduleData queda null → <Navigate to="/" />.
        setCheckedPath(currentPath);
      },
      onError: () => setCheckedPath(currentPath),
    });
  }

  const renderAliasComponent = () => {
    if (!moduleData?.module || !componentMap[moduleData.module]) {
      return <Navigate to="/" />;
    }

    const Component = componentMap[moduleData.module];
    
    return <Component 
      id={moduleData.id} 
      setLoading={setLoading}
    />;
  }

  return (
    <>
      <ErrorBoundary>
      <Routes>
        <Route path="/" element={<Home setLoading={setLoading}/>} />
        <Route path="/login" element={<Login setLoading={setLoading}/>} />


        {role_id === 1 &&
        <Route path="/cuenta" element={<AccountUsers setLoading={setLoading}/>} />
        }

        {role_id === 2 &&
        <Route path="/cuenta" element={<AccountNurses setLoading={setLoading}/>} />
        }

        {role_id === 100 &&
        <Route path="/cuenta" element={<AccountUsers setLoading={setLoading}/>} />
        }

      
        <Route path="/cuenta/editar" element={<AccountEditor setLoading={setLoading}/>} />
        <Route path="/enfermeras" element={<NursesList setLoading={setLoading}/>} />
        <Route path="/enfermeras/:user_id" element={<NursesDetail setLoading={setLoading}/>} />
        <Route path="/terminos" element={<Terms setLoading={setLoading}/>} />
        <Route path="/quienes-somos" element={<AboutUs/>} />
        <Route path="/resultado-pago" element={<PaymentResult setLoading={setLoading}/>} />
        <Route path="/mensajes" element={<MessagesList />} />
        <Route path="/mensajes/:conversation_id" element={<MessagesList />} />
        <Route path="/blog" element={<GlobalBlog />} />
        <Route path="/blog/:alias" element={<BlogDetails setLoading={setLoading}/>} />
        <Route path="/preguntas-frecuentes" element={<FAQ setLoading={setLoading}/>} />
        <Route path="/ver-cotizacion/:token" element={<NurseQuoteView />} />
        <Route path="/mis-cotizaciones/:token" element={<PatientQuotesView />} />
        <Route path="/test-flujo-solicitud" element={<TestFlujoSolicitud />} />
        <Route path="/cotiza-tu-enfermera" element={<QuoteLanding />} />
        <Route path="/admin/cotizaciones/:request_id" element={<AdminQuoteView />} />
        
        {/* Make CompanyDetails route available to all roles */}
        <Route path="/empresas/:company_id" element={<CompanyDetails setLoading={setLoading}/>} />

        {role_id === 100 &&
          <>
            <Route path="/banners" element={<BannersList setLoading={setLoading}/>} />
            <Route path="/usuarios" element={<Users setLoading={setLoading}/>} />
            <Route path="/admin-blog" element={<BlogList setLoading={setLoading}/>} />
            <Route path="/admin-mensajes" element={<AdminMessages setLoading={setLoading}/>} />
            <Route path="/configuracion" element={<Settings setLoading={setLoading}/>} />
            <Route path="/distintivos" element={<BadgesList setLoading={setLoading}/>} />
            <Route path="/empresas" element={<CompaniesList setLoading={setLoading}/>} />
            <Route path="/calificaciones" element={<AdminRatingsList setLoading={setLoading}/>} />
            <Route path="/reporte-enfermeras" element={<NursesReport setLoading={setLoading}/>} />
            <Route path="/redirecciones" element={<RedirectsList setLoading={setLoading}/>} />
            <Route path="/contactos-whatsapp" element={<WhatsappLogsList setLoading={setLoading}/>} />
            <Route path="/reporte-cotizaciones" element={<QuoteRequestsList setLoading={setLoading}/>} />
          </>
        }

        {/* Ruta para manejar alias */}
        <Route path="*" element={
          checkedPath !== location.pathname ? null : (
            moduleData ? renderAliasComponent() : <Navigate to="/" />
          )
        } />
      </Routes>
      </ErrorBoundary>
      <WhatsAppButton />
    </>
  );
}

